import { useState } from "react";
import { Combobox, ETypeOfNotification, InputColumnTableProps, InputSwitch, InputTable, InputTextCustom, Modal, setNotification } from "medere-ui-framework";
import { Button } from "primereact/button";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import { useDispatch } from "react-redux";
import { Box, defaultBox, Sector } from "../../../../../interface/interface";
import { CashRegisterDTO } from "../../../../../dto/locationcash/LocationCashDTO";

import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";
import LocationInlineEditActions from "../../../components/LocationInlineEditActions";
import { associateBoxToHardware, deassociateBoxFromHardware, saveHospitalLocation } from "../../../../../servicesHooks/Location/useLocationConfigService";

interface BoxTableProps {
    boxes: Box[];
    onChange: (updatedBoxes: Box[]) => void;
    onDelete: (box: Box, _idx: number) => void;
    sectors: Sector[];
    estId: number; //Para que el box sepa en que establecimiento esta
    mobileTitle?: string;
    mobileSubtitle?: string;
    onConfigureCash?: (box: Box) => void;
    cashRegisterMap?: Map<number, CashRegisterDTO>;
}

export function BoxTable({ boxes, onChange, onDelete, sectors, estId, mobileTitle, mobileSubtitle, onConfigureCash, cashRegisterMap }: BoxTableProps) {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);

    const [itemBox, setItemBox] = useState<Box>({ ...defaultBox, building: estId });
    const [boxErrors, setBoxErrors] = useState<{ name?: string; sector?: string }>({});
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);

    const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
    const isEditing = editIndex !== null;

    const resetEditor = () => {
        setBoxErrors({});
        setSelectedSector(null);
        setItemBox({ ...defaultBox, building: estId });
        setEditIndex(null);
    };

    const handleAdd = async (): Promise<boolean> => { //Crea el box
        const errors = validateBox(itemBox);
        setBoxErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;

        if (hasErrors) {
            return false;
        }

        try {
            const res = await saveHospitalLocation(itemBox);
            const updatedBoxes: Box[] = editIndex === null
                ? [...(boxes ?? []), res]
                : (boxes ?? []).map((box, index) => (index === editIndex ? res : box));
            onChange(updatedBoxes);

            resetEditor();

            dispatch(setNotification({
                title: !isEditing ? "Creación exitosa" : "Edición exitosa",
                desc: !isEditing ? "Se ha creado el puesto" : "Se ha editado el puesto",
                icon: "pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO
            }));
            return true;
        } catch (err) {
            console.error(err);
            dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo guardar el puesto de atención", icon: "pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
            return false;
        }
    };

    const handleEdit = (item: Box, index: number) => { //Edita el box
        setItemBox(item);
        setBoxErrors({});
        setSelectedSector({
            sector: item.sector,
            name: item.sectorstring,
            sectortype: 0,
            sectortypedescription: "",
            externalcode: "",
            repositorytype: 0,
            repositorytypedescription: "",
            parentsector: 0,
            parentsectorname: "",
            shortname: "",
            acceptpatienttransfers: false,
            restrictroomgendercontrol: true,
            prescriptionpriority: null,
            active: true,
            building: 0,
            buildigname: ""
        });
        setEditIndex(index);
    };

    const handleToggleActive = (item: Box, index: number) => { //Acceso rapido a activar/desactivar un box para no requerir editar
        const updatedBoxes = [...boxes];
        const updatedItem = { ...item, active: !item.active };

        saveHospitalLocation(updatedItem).then(() => {
            updatedBoxes[index] = updatedItem;
            onChange(updatedBoxes);

            if (updatedItem.hardwareid === localStorage.getItem("uuid")) {
                window.location.reload();
            }
        }).catch((err) => {
            console.error(err);
            dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo editar el puesto de atención", icon: "pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
        });
    };

    const handleAssociateDevice = (item: Box, index: number) => { //Asocia/desasocia el box al dispositivo actual
        let updatedBoxes: Box[];
        const currentUuid = localStorage.getItem("uuid") ?? "";

        if (item.hardwareid !== null && item.hardwareid.trim() !== "" && item.hardwareid.trim() === currentUuid) {
            deassociateBoxFromHardware({ hospitalLocationId: item.hospitallocation }).then(() => {
                updatedBoxes = [...boxes];
                updatedBoxes[index] = { ...item, hardwareid: "" };

                onChange(updatedBoxes);
                window.location.reload(); //Recargo la página para que desaparezca el Widget de PatientCaller
            }).catch((err) => {
                console.error(err);
                dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo desasociar el puesto de atención", icon: "pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
            });
        } else {
            associateBoxToHardware({ hospitalLocationId: item.hospitallocation, uuid: currentUuid }) //La uuid del navegador se asocia al deviceId del hospitalLocation
                .then((res: string) => {
                    updatedBoxes = boxes.map((box, i) => ({
                        ...box,
                        hardwareid: i === index ? res : ""
                    }));

                    onChange(updatedBoxes);
                    window.location.reload(); //Recargo para traer queuenumbers adecuados al puesto nuevo
                }).catch((err) => {
                    console.error(err);
                    if (err.response?.status === 409) {
                        dispatch(setNotification({ title: "No se puede asociar", desc: "El puesto de atención está asociado a otro dispositivo.", icon: "pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
                    } else {
                        dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo asociar el puesto de atención", icon: "pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
                    }
                });
        }
    };

    const deassociateDevice = (item: Box, index: number) => { //Desasocia el puesto de otro usuario del dispositivo
        deassociateBoxFromHardware({ hospitalLocationId: item.hospitallocation }).then(() => {
            const updatedBoxes = [...boxes];
            updatedBoxes[index] = { ...item, hardwareid: "" };

            onChange(updatedBoxes);
        }).catch((err) => {
            console.error(err);
            dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo desasociar el puesto de atención", icon: "pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
        });
    };

    const validateBox = (box: Box): { name?: string; sector?: string } => {
        const errors: { name?: string; sector?: string } = {};
        if (!box.description.trim()) errors.name = "El nombre es requerido";
        if (box.sector <= 0) errors.sector = "Debe seleccionar un sector";
        return errors;
    };

    const cancelEdit = () => {
        resetEditor();
    };

    function closeMobileModal() {
        setMobileModalVisible(false);
        resetEditor();
    }

    function openAddModal() {
        resetEditor();
        setMobileModalVisible(true);
    }

    function openEditModal(item: Box, index: number) {
        handleEdit(item, index);
        setMobileModalVisible(true);
    }

    async function saveFromMobileModal() {
        const result = await handleAdd();
        if (result) {
            setMobileModalVisible(false);
        }
    }

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    const columns: InputColumnTableProps<Box>[] = [
        {
            header: "Nombre",
            className: "location-inputtable-fluid-col location-col-box-name",
            body: (item) => item.description,
            input: (
                <InputTextCustom
                    key={"inputboxnameid"}
                    className={(boxErrors.name ? "" : "border-grey") + " w-full"}
                    value={itemBox.description}
                    onChange={(e) => {
                        setItemBox({ ...itemBox, description: e.target.value });
                        setBoxErrors({ ...boxErrors, name: "" });
                    }}
                    error={!!boxErrors.name}
                    caption={boxErrors.name}
                />
            ),
        },
        {
            header: "Sector",
            className: "location-inputtable-fluid-col location-col-box-sector",
            body: (item) => item.sectorstring,
            input: (
                <Combobox
                    key={"combosectorid"}
                    className={(boxErrors.sector ? "" : "border-grey") + " w-full"}
                    getItems={() => Promise.resolve(sectors)}
                    value={selectedSector?.sector > 0 ? selectedSector : null}
                    setValue={(e: Sector | null) => {
                        if (e) {
                            setSelectedSector(e);
                            setItemBox({ ...itemBox, sector: e.sector, sectorstring: e.name });
                            setBoxErrors({ ...boxErrors, sector: "" });
                        } else {
                            setSelectedSector(null);
                            setItemBox({ ...itemBox, sector: 0, sectorstring: "" });
                        }
                    }}
                    optionLabel="name"
                    error={!!boxErrors.sector}
                    caption={boxErrors.sector}
                />
            ),
        },
        {
            header: "Estado",
            className: "location-inputtable-fluid-col location-col-box-state",
            body: (item) => (
                <span>
                    {item.active ? "Activo" : "Inactivo"}
                </span>
            ),
            input: (
                <div className="flex align-items-center gap-2 mt-2 location-switch-inline-row location-desktop-inputtable-switch-row">
                    <InputSwitch
                        id="inputswitchactiveid"
                        className="w-full"
                        checked={itemBox.active}
                        onChange={(e) => setItemBox({ ...itemBox, active: e.value })}
                    />
                    <span className="text-sm location-switch-state-label">{itemBox.active ? "Activo" : "Inactivo"}</span>
                </div>
            ),
        },
        {
            header: "",
            className: "location-inputtable-fluid-col location-col-box-cash",
            body: (item) => cashRegisterMap?.get(item.hospitallocation) ? (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-xl ml-2.5">
                    <i className="pi pi-dollar mr-1" style={{ fontSize: '0.7rem' }} />{cashRegisterMap.get(item.hospitallocation)?.description}
                </span>
            ) : null
        },
        {
            header: "",
            className: "location-inputtable-fluid-col location-col-box-device",
            body: (item) => item.hardwareid !== null && item.hardwareid.trim() !== "" ? (
                <span className="location-device-badge ml-2.5 location-col-box-device-badge">
                    {item.hardwareid === localStorage.getItem("uuid") ? "asociado a este dispositivo" : "ya tiene un dispositivo asociado"}
                </span>
            ) : null
        },
        {
            header: "",
            className: "location-inputtable-control-column",
            input: (
                isEditing ? (
                    <LocationInlineEditActions
                        visible
                        onConfirm={() => { void handleAdd(); }}
                        onCancel={cancelEdit}
                    />
                ) : (
                    <Button
                        type="button"
                        icon="pi pi-plus-circle text-3xl"
                        className="buttonSecondary2 p-button-rounded location-inputtable-add-inline-btn"
                        onClick={() => { void handleAdd(); }}
                        aria-label="Crear"
                    />
                )
            ),
            body: (item, index) => {
                const menuItems: LocationActionItem[] = [
                    ...(onConfigureCash ? [{
                        label: "Asignar caja",
                        icon: "pi pi-dollar",
                        action: () => onConfigureCash(item),
                    }] : []),
                    {
                        label: (item.hardwareid !== null && item.hardwareid.trim() !== "" && item.hardwareid === localStorage.getItem("uuid")) ? "Desasociar dispositivo del puesto" : "Asociar dispositivo al puesto",
                        icon: "pi pi-link",
                        action: () => handleAssociateDevice(item, index),
                    },
                    {
                        label: "Editar",
                        icon: "pi pi-file-edit",
                        action: () => handleEdit(item, index),
                    },
                    {
                        label: item.active ? "Desactivar" : "Activar",
                        icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                        action: () => handleToggleActive(item, index),
                    },
                    {
                        label: "Eliminar",
                        icon: "pi pi-trash",
                        action: () => onDelete(item, index),
                    },
                    ...(item.hardwareid !== null && item.hardwareid.trim() !== "" && item.hardwareid !== localStorage.getItem("uuid")
                        ? [{
                            label: "Desasociar dispositivo del puesto",
                            icon: "pi pi-link",
                            action: () => deassociateDevice(item, index),
                        }]
                        : []
                    ),
                ];

                return (
                    <div className="flex justify-content-end">
                        <LocationActionMenu
                            items={menuItems}
                            ariaLabel="Opciones del puesto"
                            buttonId="buttonmenuitemid"
                            buttonClassName="p-button-text location-inputtable-menu-btn"
                        />
                    </div>
                );
            },
        },
    ];

    if (isMobile) {
        return (
            <div className="location-config-view">
                <div className="location-mobile-block-header">
                    <div>
                        <h4 className="location-mobile-card-title">{formatCardValue(mobileTitle, "Establecimiento")}</h4>
                        {!!mobileSubtitle && <p className="grey-color text-sm mt-1 mb-0">{mobileSubtitle}</p>}
                    </div>
                    <Button
                        className="location-mobile-add-btn"
                        icon="pi pi-plus-circle"
                        label="Agregar"
                        onClick={openAddModal}
                    />
                </div>

                <div className="location-mobile-card-list">
                    {(boxes ?? []).map((item, index) => {
                        const isAssociated = item.hardwareid !== null && item.hardwareid.trim() !== "";
                        const isCurrentDevice = item.hardwareid === localStorage.getItem("uuid");

                        const mobileMenuItems: LocationMobileActionItem[] = [
                            ...(onConfigureCash ? [{
                                label: "Configurar caja",
                                icon: "pi pi-dollar",
                                action: () => onConfigureCash(item),
                            }] : []),
                            {
                                label: isAssociated && isCurrentDevice ? "Desasociar dispositivo del puesto" : "Asociar dispositivo al puesto",
                                icon: "pi pi-link",
                                action: () => handleAssociateDevice(item, index),
                            },
                            {
                                label: "Editar",
                                icon: "pi pi-file-edit",
                                action: () => openEditModal(item, index),
                            },
                            {
                                label: item.active ? "Desactivar" : "Activar",
                                icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                                action: () => handleToggleActive(item, index),
                            },
                            {
                                label: "Eliminar",
                                icon: "pi pi-trash",
                                action: () => onDelete(item, index),
                            },
                            {
                                label: "Desasociar dispositivo del puesto",
                                icon: "pi pi-link",
                                action: () => deassociateDevice(item, index),
                                disabled: !isAssociated || isCurrentDevice,
                            },
                        ];

                        return (
                            <article key={`location-box-${item.hospitallocation}-${index}`} className="location-mobile-card">
                                <div className="location-mobile-card-header">
                                    <h4 className="location-mobile-card-title">{formatCardValue(item.description, "Sin nombre")}</h4>
                                    <LocationMobileActionMenu items={mobileMenuItems} ariaLabel="Opciones del puesto" />
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Sector</span>
                                    <span className="location-mobile-card-value">{formatCardValue(item.sectorstring)}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Estado</span>
                                    <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                                </div>
                                {cashRegisterMap?.get(item.hospitallocation) && (
                                    <div className="location-mobile-card-row">
                                        <span className="location-mobile-card-key">Caja</span>
                                        <span className="location-mobile-card-value text-green-700">
                                            <i className="pi pi-dollar mr-1" style={{ fontSize: '0.7rem' }} />{cashRegisterMap.get(item.hospitallocation)?.description}
                                        </span>
                                    </div>
                                )}
                                {isAssociated && (
                                    <div className="location-mobile-card-row">
                                        <span className="location-mobile-card-key">Dispositivo</span>
                                        <span className="location-mobile-card-value">
                                            {isCurrentDevice ? "Asociado a este dispositivo" : "Ya tiene dispositivo asociado"}
                                        </span>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                    {(boxes ?? []).length === 0 && (
                        <p className="location-mobile-empty">No hay puestos cargados.</p>
                    )}
                </div>

                <Modal
                    className="location-mobile-editor-modal"
                    visible={mobileModalVisible}
                    setVisible={setMobileModalVisible}
                    width="w-medium"
                    onHideCustom={closeMobileModal}
                    header={<h1 className="info-dark text-2xl">{itemBox.hospitallocation > 0 ? "Editar puesto" : "Agregar puesto"}</h1>}
                    footerButtonLeftText="Cancelar"
                    onClickLeftBtn={closeMobileModal}
                    footerButtonRightText={itemBox.hospitallocation > 0 ? "Guardar" : "Agregar"}
                    onClickRightBtn={() => { void saveFromMobileModal(); }}
                >
                    <div className="location-mobile-modal-content">
                        <div>
                            <label className="block mb-2">Nombre <span className="text-red-500">*</span></label>
                            <InputTextCustom
                                className={(boxErrors.name ? "" : "border-grey") + " w-full"}
                                value={itemBox.description}
                                onChange={(e) => {
                                    setItemBox({ ...itemBox, description: e.target.value });
                                    setBoxErrors({ ...boxErrors, name: "" });
                                }}
                                error={!!boxErrors.name}
                                caption={boxErrors.name}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">Sector <span className="text-red-500">*</span></label>
                            <Combobox
                                className={(boxErrors.sector ? "" : "border-grey") + " w-full"}
                                getItems={() => Promise.resolve(sectors)}
                                value={selectedSector?.sector > 0 ? selectedSector : null}
                                setValue={(e: Sector | null) => {
                                    if (e) {
                                        setSelectedSector(e);
                                        setItemBox({ ...itemBox, sector: e.sector, sectorstring: e.name });
                                        setBoxErrors({ ...boxErrors, sector: "" });
                                    } else {
                                        setSelectedSector(null);
                                        setItemBox({ ...itemBox, sector: 0, sectorstring: "" });
                                    }
                                }}
                                optionLabel="name"
                                error={!!boxErrors.sector}
                                caption={boxErrors.sector}
                            />
                        </div>
                        <div className="flex align-items-center gap-2 location-switch-inline-row">
                            <InputSwitch
                                id="inputswitchactiveboxmobileid"
                                checked={itemBox.active}
                                onChange={(e) => setItemBox({ ...itemBox, active: e.value })}
                            />
                            <span className="location-switch-state-label">{itemBox.active ? "Activo" : "Inactivo"}</span>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }

    //border-divider hace que se vea el divider en la tabla
    return (
        <InputTable
            className="location-inputtable-fluid location-inputtable-fluid--box"
            columnGap={0}
            headerDivClassName="font-semibold bg-bluegray-50"
            dividerClassName="my-1 border-divider"
            buttonColumnWidth={43}
            items={boxes ?? []}
            columns={columns}
            onAdd={() => {}}
            divBtnClassName="location-inputtable-hide-native-control"
            divider
            dividerInFirstItem
        />
    );
}
